import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import styled from 'styled-components';
import { Border, Color, Grid, Type } from '../app/StyleGuide';
import { handleError } from '../utils/apiUtils';
import { sanitizeHTML } from '../utils/sanitizeHTML';
import { LinkStyleButton, MediumButton, MediumPrimaryButton } from './Buttons';
import ErrorMessage from './ErrorMessage';
import { Input } from './FormElements';
import LoadingState from './LoadingState';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from './ModalElements';
import { downloadQRCode, QRCode } from './QRCode';

type EasyLinkModalProps = {
  link: { orgPrefix: string; groupCode: string } | string;
  description: string;
  handleDismiss: () => void;
  qrCodeIdentifier?: string;
  isLoading?: boolean;
  hideQRCode?: boolean;
};

const EasyLinkModalBody = styled(ModalBody)`
  display: flex;
  justify-content: center;
  flex-direction: column;
  label {
    margin: 0 0 ${Grid._3};
    color: ${Type.Color.dark};
    font-weight: ${Type.Weight.semibold};
  }
  .description {
    margin-bottom: ${Grid._4};
    ul {
      color: ${Type.Color.dark};
    }
  }
`;

const ContentContainer = styled.div`
  display: flex;
  gap: ${Grid._4};
  background: ${Color.Gray._05};
  padding: ${Grid._3};
  border: 1px solid ${Color.Blue._20};
  border-radius: ${Border.radius};
  margin-top: ${Grid._4};

  h6 {
    font-size: ${Type.Scale._2};
    font-weight: ${Type.Weight.bold};
    color: ${Type.Color.dark};
    margin: 0;
  }

  p {
    font-size: ${Type.Scale._1};
    padding: 0;
    color: ${Type.Color.dark};
  }

  ${LinkStyleButton} {
    padding: 0;
  }
`;

const EasyLinkModal = ({
  qrCodeIdentifier,
  link,
  description,
  handleDismiss,
  isLoading = false,
  hideQRCode = false,
}: EasyLinkModalProps) => {
  const [copied, setCopied] = useState(false);

  const easyLink =
    typeof link === 'string'
      ? link
      : typeof link === 'object' && link.orgPrefix && link.groupCode
      ? window.location.origin + '/#/easy-link/' + link.orgPrefix + '/' + link.groupCode
      : '';

  useEffect(() => {
    if (!copied) return;
    const timeoutId = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  const onDownloadQRCode = () => {
    const downloadFileName = qrCodeIdentifier ? `${qrCodeIdentifier.replace(/[^a-z0-9]/gi, '-')}-qrcode` : undefined;
    downloadQRCode(downloadFileName).catch(error => {
      handleDismiss();
      handleError(error);
    });
  };

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader>
        <h3>Easy Link</h3>
      </ModalHeader>
      {isLoading ? (
        <ModalBody>
          <LoadingState />
        </ModalBody>
      ) : (
        <EasyLinkModalBody>
          <div className="description" dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />
          {easyLink ? (
            <>
              <Input id="EasyLinkURL" value={easyLink} readOnly />
              {hideQRCode !== true && (
                <ContentContainer>
                  <QRCode size={72} link={easyLink} />
                  <div>
                    <h6>Need to share your QR code?</h6>
                    <p>Download an image of your QR code to add to a slide or email</p>
                    <LinkStyleButton onClick={onDownloadQRCode} data-tracking-id="download-qrcode">
                      Download QR Code
                    </LinkStyleButton>
                  </div>
                </ContentContainer>
              )}
            </>
          ) : (
            <ErrorMessage>There was a problem retrieving your easy link. Please refresh and try again.</ErrorMessage>
          )}
        </EasyLinkModalBody>
      )}
      <ModalFooter>
        {easyLink && (
          <CopyToClipboard text={easyLink} onCopy={() => setCopied(true)}>
            <MediumPrimaryButton disabled={copied || isLoading}>
              {copied ? 'Copied' : 'Copy Easy Link'}
            </MediumPrimaryButton>
          </CopyToClipboard>
        )}
        <MediumButton aria-label="close modal" onClick={handleDismiss}>
          Close
        </MediumButton>
      </ModalFooter>
    </ModalContainer>
  );
};

export default EasyLinkModal;
